import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useCurrency } from '../context/CurrencyContext';

const Privacy = () => {
  const { t, selectedCountry } = useCurrency();
  const { countryCode } = useParams();
  const isIndia = selectedCountry.code === 'IN';
  const supportEmail = isIndia ? 'support.in@wisedrive.com' : 'support.my@wisedrive.com';

  const sections = [
    {
      subHeader: 'What do we do with your information?',
      subContent:
        'When you purchase something from our store, as part of the buying and selling process, we collect the personal information you give us such as your name, address and email address. When you browse our store, we also automatically receive your computer\'s internet protocol (IP) address in order to provide us with information that helps us learn about your browser and operating system. Email marketing (if applicable): With your permission, we may send you emails about our store, new products and other updates.',
    },
    {
      subHeader: 'Consent',
      subContent: `How do you get my consent? When you provide us with personal information to complete a transaction, verify your credit card, place an order, arrange for a delivery or return a purchase, we imply that you consent to our collecting it and using it for that specific reason only. If we ask for your personal information for a secondary reason, like marketing, we will either ask you directly for your expressed consent, or provide you with an opportunity to say no. How do I withdraw my consent? If after you opt-in, you change your mind, you may withdraw your consent for us to contact you, for the continued collection, use or disclosure of your information, at anytime, by contacting us at ${supportEmail}`,
    },
    {
      subHeader: 'Disclosure',
      subContent:
        'We may disclose your personal information if we are required by law to do so or if you violate our Terms of Service.',
    },
    {
      subHeader: 'Payment',
      subContent:
        'We use Razorpay for processing payments. We/Razorpay do not store your card data on their servers. The data is encrypted through the Payment Card Industry Data Security Standard (PCI-DSS) when processing payment. Your purchase transaction data is only used as long as is necessary to complete your purchase transaction. After that is complete, your purchase transaction information is not saved. Our payment gateway adheres to the standards set by PCI-DSS as managed by the PCI Security Standards Council, which is a joint effort of brands like Visa, MasterCard, American Express and Discover. PCI-DSS requirements help ensure the secure handling of credit card information by our store and its service providers.',
    },
    {
      subHeader: 'Third-party services',
      subContent:
        'In general, the third-party providers used by us will only collect, use and disclose your information to the extent necessary to allow them to perform the services they provide to us. However, certain third-party service providers, such as payment gateways and other payment transaction processors, have their own privacy policies in respect to the information we are required to provide to them for your purchase-related transactions. For these providers, we recommend that you read their privacy policies so you can understand the manner in which your personal information will be handled by these providers. In particular, remember that certain providers may be located in or have facilities that are located a different jurisdiction than either you or us. So if you elect to proceed with a transaction that involves the services of a third-party service provider, then your information may become subject to the laws of the jurisdiction(s) in which that service provider or its facilities are located. Once you leave our website or are redirected to a third-party website or application, you are no longer governed by this Privacy Policy or our website\'s Terms of Service. When you click on links on our website, they may direct you away from our site. We are not responsible for the privacy practices of other sites and encourage you to read their privacy statements.',
    },
    {
      subHeader: 'Security',
      subContent:
        'To protect your personal information, we take reasonable precautions and follow industry best practices to make sure it is not inappropriately lost, misused, accessed, disclosed, altered or destroyed.',
    },
    {
      subHeader: 'Cookies',
      subContent:
        'We use cookies to maintain session of your user. It is not used to personally identify you on other websites.',
    },
    {
      subHeader: 'Age of consent',
      subContent:
        'By using this site, you represent that you are at least the age of majority in your state or province of residence, or that you are the age of majority in your state or province of residence and you have given us your consent to allow any of your minor dependents to use this site.',
    },
    {
      subHeader: 'Changes to this privacy policy',
      subContent:
        'We reserve the right to modify this privacy policy at any time, so please review it frequently. Changes and clarifications will take effect immediately upon their posting on the website. If we make material changes to this policy, we will notify you here that it has been updated, so that you are aware of what information we collect, how we use it, and under what circumstances, if any, we use and/or disclose it. If our store is acquired or merged with another company, your information may be transferred to the new owners so that we may continue to sell products to you.',
    },
    {
      subHeader: 'Questions & contact information',
      subContent: `If you would like to access, correct, amend or delete any personal information we have about you, register a complaint, or simply want more information, contact our Privacy Compliance Officer at ${supportEmail}`,
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar forceWhite hasBanner={false} />

      {/* Hero */}
      <div className="bg-slate-900 pt-28 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to={`/${countryCode}`}
            className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 text-sm mb-8 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('legal.backToHome')}
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-teal-500/20 p-3 rounded-xl">
              <Shield className="h-6 w-6 text-teal-400" />
            </div>
            <span className="text-teal-400 text-sm font-medium uppercase tracking-wider">Legal</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">{t('legal.privacy.title')}</h1>
          <p className="text-slate-400 text-lg">{t('legal.privacy.subtitle')}</p>
          <p className="text-slate-500 text-sm mt-4">
            {t('legal.effectiveDate')}: January 1, 2024
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-8 md:p-12">
            <p className="text-slate-600 mb-10 pb-8 border-b border-slate-100 leading-relaxed">
              {isIndia
                ? 'Wisedrive Technologies Private Limited'
                : 'Wisedrive Technologies (M) Sdn. Bhd. Limited'}{' '}
              is committed to protecting your privacy. This policy explains how we collect, use, and safeguard
              your personal information when you use our services.
            </p>

            <div className="space-y-10">
              {sections.map((section, index) => (
                <div key={index} className="pb-8 border-b border-slate-100 last:border-0 last:pb-0">
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">{section.subHeader}</h2>
                  <p className="text-slate-600 leading-relaxed">{section.subContent}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Contact Card */}
        <div className="mt-8 bg-teal-50 border border-teal-100 rounded-2xl p-8 text-center">
          <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('legal.contactUs')}</h3>
          <p className="text-slate-600 mb-4">
            Questions about this Privacy Policy? We're here to help.
          </p>
          <a
            href={`mailto:${supportEmail}`}
            className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-teal-700 transition-colors"
          >
            {supportEmail}
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Privacy;
